import { theme } from '@Guidelines/Theme/theme';
import formatInterviewReference from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/Helpers/formatInterviewReference';
import formatEvidenceTimestamp from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Helpers/formatEvidenceTimestamp';
import type TurnEvidenceView from '@UI/Store/Documents/Views/TurnEvidenceView';
import { css } from '@emotion/react';
import * as React from 'react';
import useSubjectInterviews from '@UI/Pages/Beneficiaries/Hooks/useSubjectInterviews';
import useDocumentRoute from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentRoute';

const cardStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
	padding: 1rem 1.125rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
`;

const headerRowStyle = css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.625rem;
`;

const referenceStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
`;

const speakerStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
`;

const whenStyle = css`
	${theme.typography.eyebrow}
	color: ${theme.colors.inkFaint};
`;

const quoteStyle = css`
	margin: 0;
	padding-left: 0.75rem;
	border-left: 3px solid ${theme.colors.waveform};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.large};
	line-height: 1.65;
	color: ${theme.colors.inkSecondary};
`;

type Props = {
	readonly evidence: TurnEvidenceView;
};

/** One citation from an interview transcript: a reference line naming the interview, then the
 * speaker and the timestamp, then the quote set off by an amber rule. The interview is resolved
 * against the dossier's interviews and reads "Entretien n° {id}" while unresolved. Playback is not
 * offered. */
const TurnEvidenceCard: React.FC<Props> = ({ evidence }) => {
	const { subject } = useDocumentRoute();
	const { data: interviews } = useSubjectInterviews(subject);
	const interview = interviews?.findById(evidence.interviewId) ?? null;

	const speakerLabel = evidence.speakerLabel ?? evidence.speakerKind?.label ?? 'Locuteur non attribué';
	const reference = formatInterviewReference(interview, evidence.interviewId);
	const timePart = React.useMemo(
		() => (evidence.startSecond !== null ? formatEvidenceTimestamp(evidence.startSecond) : null),
		[evidence.startSecond],
	);
	return (
		<div css={cardStyle}>
			<p css={referenceStyle}>{reference}</p>
			<div css={headerRowStyle}>
				<span css={speakerStyle}>{speakerLabel}</span>
				{timePart !== null ? <span css={whenStyle}>{timePart}</span> : null}
			</div>
			<p css={quoteStyle}>{evidence.quote}</p>
		</div>
	);
};

export default TurnEvidenceCard;
